import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "../lib/utils";
import { api } from "../lib/api";

interface SubdomainInputProps {
  siteId: string;
  initialSubdomain?: string | null;
  onSubdomainReserved?: (subdomain: string) => void;
  disabled?: boolean;
}

type ValidationState = "idle" | "checking" | "available" | "unavailable" | "invalid";

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Client-side format validation (mirrors server-side logic)
function validateFormat(subdomain: string): { valid: boolean; reason?: string } {
  if (!subdomain) {
    return { valid: true }; // Empty is valid (not required yet)
  }
  
  if (subdomain.length < 3) {
    return { valid: false, reason: "Must be at least 3 characters" };
  }
  
  if (subdomain.length > 30) {
    return { valid: false, reason: "Must be 30 characters or less" };
  }
  
  if (subdomain !== subdomain.toLowerCase()) {
    return { valid: false, reason: "Must be lowercase" };
  }
  
  const regex = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;
  if (!regex.test(subdomain)) {
    if (subdomain.startsWith("-") || subdomain.endsWith("-")) {
      return { valid: false, reason: "Cannot start or end with a hyphen" };
    }
    if (/[^a-z0-9-]/.test(subdomain)) {
      return { valid: false, reason: "Only lowercase letters, numbers, and hyphens allowed" };
    }
    return { valid: false, reason: "Invalid format" };
  }
  
  return { valid: true };
}

export function SubdomainInput({
  siteId,
  initialSubdomain,
  onSubdomainReserved,
  disabled = false,
}: SubdomainInputProps) {
  const [value, setValue] = useState(initialSubdomain || "");
  const [validationState, setValidationState] = useState<ValidationState>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [isReserving, setIsReserving] = useState(false);
  const lastCheckedRef = useRef<string>("");
  
  const debouncedValue = useDebounce(value, 400);

  // Check availability when debounced value changes
  const checkAvailability = useCallback(async (subdomain: string) => {
    if (!subdomain) {
      setValidationState("idle");
      setMessage(null);
      return;
    }

    // Client-side format validation first
    const formatCheck = validateFormat(subdomain);
    if (!formatCheck.valid) {
      setValidationState("invalid");
      setMessage(formatCheck.reason || "Invalid format");
      return;
    }

    // Skip if we already checked this value
    if (lastCheckedRef.current === subdomain) {
      return;
    }

    setValidationState("checking");
    setMessage(null);

    try {
      const result = await api.checkSubdomainAvailable(subdomain);
      lastCheckedRef.current = subdomain;
      
      if (result.available) {
        setValidationState("available");
        setMessage("Available!");
      } else {
        setValidationState("unavailable");
        setMessage(result.reason || "Not available");
      }
    } catch (err) {
      setValidationState("invalid");
      setMessage("Could not check availability");
    }
  }, []);

  useEffect(() => {
    checkAvailability(debouncedValue);
  }, [debouncedValue, checkAvailability]);

  // Handle input change
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setValue(newValue);
    
    // Immediate format validation for feedback
    if (newValue) {
      const formatCheck = validateFormat(newValue);
      if (!formatCheck.valid) {
        setValidationState("invalid");
        setMessage(formatCheck.reason || "Invalid format");
      } else {
        setValidationState("checking");
        setMessage(null);
      }
    } else {
      setValidationState("idle");
      setMessage(null);
    }
  }

  // Reserve subdomain
  async function handleReserve() {
    if (validationState !== "available" || isReserving) return;
    
    setIsReserving(true);
    try {
      await api.reserveSubdomain(siteId, value);
      onSubdomainReserved?.(value);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to reserve");
      setValidationState("invalid");
    } finally {
      setIsReserving(false);
    }
  }

  const isReserved = initialSubdomain && initialSubdomain === value;

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Your website URL
      </label>
      
      <div className="flex items-stretch">
        <div className="relative flex-1">
          <input
            type="text"
            value={value}
            onChange={handleChange}
            disabled={disabled || isReserving}
            placeholder="your-business"
            className={cn(
              "w-full h-9 px-3 text-sm rounded-l-lg border bg-background transition-colors",
              "placeholder:text-muted-foreground/50",
              "focus:outline-none focus:ring-2 focus:ring-ring/50",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              validationState === "available" && "border-emerald-500 focus:ring-emerald-500/30",
              validationState === "unavailable" && "border-amber-500 focus:ring-amber-500/30",
              validationState === "invalid" && "border-destructive focus:ring-destructive/30",
              validationState === "idle" && "border-border",
              validationState === "checking" && "border-border"
            )}
          />
          
          {/* Status indicator */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            {validationState === "checking" && (
              <svg className="size-4 animate-spin text-muted-foreground" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {validationState === "available" && (
              <svg className="size-4 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {validationState === "unavailable" && (
              <svg className="size-4 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {validationState === "invalid" && (
              <svg className="size-4 text-destructive" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
        </div>
        
        <span className="inline-flex items-center px-3 text-sm text-muted-foreground bg-muted border border-l-0 border-border rounded-r-lg">
          .imonsite.co.uk
        </span>
      </div>
      
      {/* Status message */}
      {message && (
        <p
          className={cn(
            "text-xs",
            validationState === "available" && "text-emerald-600 dark:text-emerald-400",
            validationState === "unavailable" && "text-amber-600 dark:text-amber-400",
            validationState === "invalid" && "text-destructive"
          )}
        >
          {message}
        </p>
      )}
      
      {/* Reserve button */}
      {validationState === "available" && !isReserved && (
        <button
          onClick={handleReserve}
          disabled={isReserving}
          className={cn(
            "w-full py-2 px-4 text-sm font-medium rounded-lg transition-colors",
            "bg-emerald-600 text-white hover:bg-emerald-700",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          {isReserving ? "Reserving..." : "Reserve this subdomain"}
        </button>
      )}
      
      {/* Reserved confirmation */}
      {isReserved && (
        <div className="flex items-center gap-2 py-2 px-3 text-sm rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">
          <svg className="size-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          <span>Subdomain reserved</span>
        </div>
      )}
    </div>
  );
}

